import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { JobOutput } from '@/api/types'
import { modelLabel } from '@/config/catalog'
import { isActive, isResultJob, useJobCenter, type Job } from '@/context/JobCenterContext'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import type { PromptEdits } from '@/lib/promptEdits'
import { AiPromptButton } from './AiPromptButton'
import { Icon } from './icons'
import { PagedList } from './PagedList'
import { AudioResult, ResultGrid, TextResult } from './ResultGrid'
import { TaskCard } from './TaskCard'
import { Badge, Button, Card, CardBody, CardHead, Chips, ClearButton, CopyButton, Empty, Modal, Progress, STATE_TONE, Textarea } from './ui'
import { MODULE_MAX_TIMES, moduleShotCount, moduleSlots, type DetailSpec } from '@/config/platformDetails'
import {
  MAIN_SHOTS,
  MAX_BATCH_COUNT,
  bumpMix,
  mixTotal,
  normalizeMix,
  type MainMix,
} from '@/config/studioShape'

/* --------------------------- 工作台两栏骨架 --------------------------- */

/**
 * 工作台两栏骨架。
 *
 * `className` 只用来加**版面容差**（如 `/tts` 的 `tts-grid`：右栏更窄、中列不自滚），
 * 不许往里塞状态类 —— 两栏结构本身必须全站一致，见文件头结论。
 */
export function GeneratorFrame({
  form,
  panel,
  className,
}: {
  form: ReactNode
  panel: ReactNode
  className?: string
}) {
  return (
    <div className={`studio${className ? ` ${className}` : ''}`}>
      <div className="studio__form">{form}</div>
      <div className="studio__panel">{panel}</div>
    </div>
  )
}

/* ------------------------------ 提交操作条 ------------------------------ */

/**
 * 提交操作条 —— **价格与生成按钮同一行**（2026-10-08 用户口径）。
 *
 * 用户原话：「每一个页面的 [生成按钮] 都放在右上角如何，[价格] 为同一行」。
 * 之前的写法是「一整行全宽大按钮」+ 上面一行报价 + 底下一行草稿状态，
 * 三行都占满左栏宽度；现在收成窄栏里的一行：
 *
 *   `[计价依据 … ¥0.10]  [✨ 生成商品文案总览]`
 *
 * 「放右上角」由**调用方**决定：9 个生成页都把它挂进 `.studio__dock`
 * （右栏顶部、历史之上、吸顶）。位置不写死在这个组件里 ——
 * `/batch` 的右栏是 `.batchpage__side`，`/digital-human` 则是全站第一个这么放的页面。
 *
 * ### 一并删掉的两样东西（不是遗漏，是**故意的**）
 *
 *   · 「草稿已自动保存 · HH:MM」：草稿是后台行为，不值得常驻一行；
 *     顺带删掉的还有那个 30s `setInterval` —— 它唯一的作用就是让那个时间戳重算。
 *   · 「清空」：它离「生成」只有 8px，点错的代价是整页表单没了。
 *     两个 prop（`savedAt` / `onReset`）**连同实现一起删**，不留成「传了也没用」的
 *     空参数 —— 留着就会有人重新接上，那时又是一个「同一件事两个入口」。
 *     ⚠️ `/batch` 例外：那张导入表是跨天台账，清空能力留在页面自己的 `extra` 里。
 *
 * ### 类名是给验收脚本的锚点，改版时**保留**
 *
 * `.submit-bar`、`.price-row`、`.price-row__value`、按钮上的 `.btn--primary`
 * 被多个脚本（`verify-detail-modes.py` 读报价、`verify-batch-csv.py` 点生成、
 * `_probe-prompt-edit.py` 限定点击范围）当定位器用。名字不变、**层级关系也不变**
 * （`.price-row` 与 `button.btn--primary` 仍然是 `.submit-bar` 的后代）。
 */
export function SubmitBar({
  price,
  basis,
  free,
  loading,
  disabled,
  disabledReason,
  label = '开始生成',
  onGenerate,
  extra,
}: {
  price: number
  basis?: string
  free?: boolean
  loading?: boolean
  disabled?: boolean
  disabledReason?: string
  label?: string
  onGenerate: () => void
  extra?: ReactNode
}) {
  return (
    <div className="submit-bar">
      {/*
        价格与按钮同处一行 —— 这一层 `.submit-bar__row` 就是「同一行」本身，
        不是排版细节：屏幕再窄也不许换行（依据文字省略、按钮永不压缩），
        换行等于退回「按钮压在价格下面」那一版。见 global.css 的 `.submit-bar__row`。
      */}
      <div className="submit-bar__row">
        {/* title 兜住被省略号截掉的计价依据：窄栏里它一定会被截 */}
        <div className="price-row" title={basis ?? '预估消耗'}>
          <span className="price-row__basis">{basis ?? '预估消耗'}</span>
          <span className={`price-row__value${free ? ' price-row__value--free' : ''}`}>
            {free ? '免费' : `¥${price.toFixed(2)}`}
          </span>
        </div>
        <Button variant="primary" size="lg" icon="sparkles" loading={loading} disabled={disabled} onClick={onGenerate}>
          {label}
        </Button>
      </div>
      {disabled && disabledReason ? (
        <div className="submit-bar__reason text-xs muted">{disabledReason}</div>
      ) : null}
      {extra}
    </div>
  )
}

/* ------------------------------ 进行中任务 ------------------------------ */

/**
 * 任务行的呈现已统一到 `TaskCard`（见 components/TaskCard.tsx）。
 *
 * 这里原来有一个只读的 `JobRow`：一张缩略图 + 一行元信息 + 取消/重试/移除三个按钮，
 * **点不开、看不到结果**。任务中心与我的作品各自又维护了一份长相相近的行 ——
 * 「点击没展开」在两个页面同时出现，就是三份实现的必然结果。
 *
 * 现在四个位置（顶栏最近任务 / 生成页本页历史 / 任务中心 / 我的作品）共用任务卡：
 * 取消、重试、移除默认从任务中心取，调用方不必再传。
 */

export function ActiveJobsStrip({
  jobs,
  onCancel,
  emptyHint,
  max = 3,
}: {
  jobs: Job[]
  onCancel: (id: string) => void
  emptyHint?: string
  max?: number
}) {
  const live = groupByBatch(jobs).filter((g) => g.jobs.some((j) => isActive(j.state)))

  // `max` 限的是**单个任务的卡**数：批量组本身就是一张卡（内部逐行），
  // 按「卡」算的话 20 行会吃掉全部额度，把用户刚提的那单任务挤没。
  // 批次组不设上限 —— 它们是用户自己开的一批活，藏起来反而像丢了。
  const shown: typeof live = []
  let singles = 0
  for (const g of live) {
    if (g.jobs.length === 1) {
      if (singles >= max) continue
      singles += 1
    }
    shown.push(g)
  }
  if (!shown.length) return null

  // 标题只数**还在跑的行** —— 批次里已完成的行不算「正在生成」，
  // 否则第一批 20 行跑完 18 行后标题仍写「正在生成 20 个任务」，是假的。
  const running = live.reduce(
    (n, g) => n + g.jobs.filter((j) => isActive(j.state)).length,
    0,
  )

  return (
    /* `active-strip` 是给验收脚本的稳定锚点：右栏里 `.job-list` 不止一处
       （批次卡内部也有一份），只数 `.job-list > *` 会**把批次卡自己的行算进去**，
       于是「聚成一张」这个断言会恒假失败。锚点必须落在外壳上。 */
    <div className="card active-strip" style={{ borderColor: 'hsl(var(--primary) / .35)' }}>
      <div className="card__head">
        <span className="spinner" />
        <div className="grow">
          <div className="card__title">正在生成 {running} 个任务</div>
          <div className="card__hint">{emptyHint ?? '可以继续提交新任务，互不阻塞'}</div>
        </div>
      </div>
      <div className="card__body card__body--tight">
        <div className="job-list">
          {shown.map((g) =>
            g.jobs.length > 1 ? (
              <BatchCard key={g.key} jobs={g.jobs} onCancel={onCancel} fallbackHint={emptyHint} />
            ) : (
              <TaskCard key={g.key} job={g.jobs[0]} onCancel={onCancel} />
            ),
          )}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------ 批次卡 ------------------------------ */

/**
 * 把任务按批次归组，**保持原顺序**（第一个出现的批次排最前）。
 *
 * `batchId` 为空的任务各自成组 —— 判空不能省：老库补列后的历史行 batchId
 * 全是空串，若把空串也当批次 id，所有**单个**提交的任务会被聚成一张
 * 「批量 · 47 个任务」的假批次卡。
 */
function groupByBatch(jobs: Job[]): { key: string; batchId?: string; jobs: Job[] }[] {
  const groups: { key: string; batchId?: string; jobs: Job[] }[] = []
  const byBatch = new Map<string, { key: string; batchId?: string; jobs: Job[] }>()
  for (const j of jobs) {
    if (!j.batchId) {
      groups.push({ key: j.id, jobs: [j] })
      continue
    }
    let g = byBatch.get(j.batchId)
    if (!g) {
      g = { key: `batch-${j.batchId}`, batchId: j.batchId, jobs: [] }
      byBatch.set(j.batchId, g)
      groups.push(g)
    }
    g.jobs.push(j)
  }
  return groups
}

/** 批次逐行的短标签。 */
function rowLabel(j: Job): string {
  // 批量任务的 title 形如「淘宝天猫 电商主图 · 硅藻土沥水垫」——
  // 20 行都带同一个前缀太吵，只取 `·` 之后那段（产品名）。
  const tail = j.title.split('·').pop()
  return (tail || j.title).trim()
}

/**
 * 批次卡 —— 「一批 N 个任务」的进度看板。
 *
 * 为什么不是 N 张 `TaskCard`：批量表格一次提 20 行，右栏就会多出 20 张卡，
 * 每张还带缩略图与三个按钮 —— 右栏被撑到几屏，而用户此刻只想知道
 * 「这一批跑到哪了、哪几行挂了」。所以这里只做**进度**，逐行紧凑呈现。
 *
 * 产出图**不在这里**：它们走「本轮结果」（`outputsFor`）。在这里再放一份
 * 就等于同一份产出两处渲染，两处的分页/懒加载迟早不一致。
 *
 * 它和 `TaskCard` 不是「同一个东西的两份实现」：`TaskCard` 是**一个任务**的卡，
 * 这里是**一批任务**的卡，层级不同。逐行刻意不做成 TaskCard —— 见上。
 */
function BatchCard({
  jobs,
  onCancel,
  fallbackHint,
}: {
  jobs: Job[]
  onCancel: (id: string) => void
  fallbackHint?: string
}) {
  const running = jobs.filter((j) => isActive(j.state))
  const done = jobs.filter((j) => j.state === 'succeeded' || j.state === 'partial').length
  const failed = jobs.filter((j) => j.state === 'failed').length
  // 失败行排前面：一批 20 行里挂了 3 行，用户首先要看的是那 3 行，
  // 而不是从 17 个「已完成」里把它翻出来。
  const ordered = [...jobs].sort(
    (a, b) => Number(b.state === 'failed') - Number(a.state === 'failed'),
  )

  return (
    /* `batch-card` 同样是验收锚点：靠它数「这一批聚成了几张卡」。 */
    <div className="card batch-card" style={{ borderColor: 'hsl(var(--primary) / .35)' }}>
      <div className="card__head">
        {running.length ? <span className="spinner" /> : null}
        <div className="grow">
          <div className="card__title">
            批量 · {jobs.length} 个任务
            {running.length ? <span className="muted text-xs"> · 还剩 {running.length} 行</span> : null}
          </div>
          <div className="card__hint">
            进行中 {running.length} · 已完成 {done}
            {failed ? ` · 失败 ${failed}` : ''}
            {!running.length ? '' : `　${fallbackHint ?? ''}`}
          </div>
        </div>
        {running.length ? (
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => running.forEach((j) => onCancel(j.id))}
          >
            取消整批
          </button>
        ) : null}
      </div>
      <div className="card__body card__body--tight">
        <div className="job-list">
          {ordered.map((j) => {
            const tone = STATE_TONE[j.state] ?? STATE_TONE.queued
            return (
              <div className="batchline row row--between" key={j.id}>
                <span className="batchline__name text-xs" title={j.title}>
                  {rowLabel(j)}
                </span>
                <Badge tone={tone.tone}>{tone.label}</Badge>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------ 报价确认 ------------------------------ */

export type PriceConfirmInfo = {
  title: string
  appSlug: string
  model: string
  outputs: number
  aspectRatio?: string
  resolution?: string
  refImages: number
  prompt: string
  /**
   * 那段文字的折叠标题（默认「将发送给模型的完整提示词」，外层会拼上「查看 / 编辑」）。
   *
   * 为什么需要它：数字人对口型那一档的上游只吃**脸图 + 音频 + 动作提示词**，
   * 那段 `prompt`（拼好的模板）它一个字都不读 —— 真正会被念出来的是**口播稿**。
   * 标题若还写着「发送给模型」，用户就会以为自己在核对模型输入，实际核对的是
   * 一张说明书；而他真正该在掏钱前看一眼的台词，反而没被展示。
   *
   * 与 `RefUploader.firstBadge` 同一条规矩：**只改文案，不改能力**。
   */
  promptLabel?: string
  /**
   * **一批多张的逐张提示词**（有它 = 一个产品出多张，每张各一份词）。
   *
   * 为什么要单独一条：主图 / 平台详情模块这两档提交的是 `args.prompts`
   * —— N 份**不一样**的词（主图四张各一份、A+ 逐模块各一份），
   * 而顶层 `prompt` 只是这一批的兜底通用词。只展示兜底词的话，
   * 「预览的」与「实发的」不是同一份 —— 而这条（预览 == 提交原文）
   * 在花钱的按钮前面是最不该破的。
   *
   * 2026-10-07 加：页面上那个「预览提示词」按钮同日按用户口径删掉了
   * （原话「提示词隐藏也可以的」），逐张词的展示**收口到这里**、
   * 默认折叠 —— 与 `batch` 那条同一套 `<details>`，页面上不再有第二处。
   */
  shotPrompts?: { label: string; prompt: string }[]
  /**
   * 逐张提示词的「AI 重写」能力。**有它 = 每张旁边出现一颗按钮；没有 = 不出现。**
   *
   * 为什么不给个默认实现（比如弹窗自己拼 brief）：改写要的上下文是
   * **这一张的定位 + 这一批的产品信息 + 本平台禁词**，那三样只有发起页知道；
   * 弹窗自己拼只能拿到 `info` 上那几个字段，拼出来的是「只有比例和平台」的空壳 ——
   * 模型照着它重写，产品名与卖点全靠编。宁可这一档不显示按钮，
   * 也不要给一颗点了会编数据的按钮。
   *
   * `briefs` 与 `shotPrompts` **下标一一对应**（缺位按空串传，后端有 `REFINE_EMPTY_INPUT`
   * 兜底 —— 真发生时是「这一张没配上上下文」的编码错，不该静默降级）。
   */
  shotRefine?: {
    /** 逐张上下文，由发起页用本页真源拼（见 `lib/detailPrompts.ts` 的 `buildShotRefineBrief`） */
    briefs: string[]
    /** 出处，只用于用量账本 */
    sourcePage: string
    /** 不让点的原因（有值 = 按钮变灰，值出现在 hover 提示里） */
    blocked?: string
  }
  price: number
  basis?: string
  free?: boolean
  /** 投放平台名（已解析好的可读名，不是 id）—— 提交前最后一道可见关口 */
  platform?: string
  /**
   * **批量形态的逐行摘要。有它 = 本次是一批**（一行产品一个任务），
   * 弹窗改展示「行清单 + 逐行提示词」，顶层 `prompt` 不再展示。
   *
   * 为什么要在这里挂而不是新开一个「批量确认」弹窗：批量与单个是**同一个
   * 决策关口**（确认要花多少钱、要发什么词），拆两个弹窗就会出现
   * 「单行点生成弹一个、批量弹另一个」—— 两个弹窗各自维护一遍
   * 报价行、免费标记、提示词展示，改一处漏一处。
   *
   * `prompt` 是每一行**该行全部张拼起来**的词（带分节），默认折叠。
   */
  batch?: {
    /** 一行 = 一个产品 */
    rows: { label: string; outputs: number; prompt: string }[]
  }
}

/*
 * `PromptEdits` 与那一趟「并回提交参数」的变换在 `@/lib/promptEdits` ——
 * 抽出去是为了让冒烟脚本能**真跑**它（grep 只能证明源码里有那么一行字，
 * 证不了「改完之后载荷里的第 1 张确实是新那段」）。
 */

/** 弹窗里正在编辑的草稿。`src` = 它对应的那一份 `info`（判据见下方注释） */
type PromptDraft = { src: PriceConfirmInfo; shots: string[]; base: string }

export function PriceConfirmDialog({
  info,
  onCancel,
  onConfirm,
}: {
  info: PriceConfirmInfo | null
  onCancel: () => void
  /** `edits` 为空 = 用户一个字没改，提交方按原样发 */
  onConfirm: (edits?: PromptEdits) => void
}) {
  /*
   * ⚠️ `useState` 必须在 `if (!info) return null` **之前**。
   *
   * 这个弹窗是「常驻挂载、`info` 为空就返回 null」的写法（调用方写的是
   * `<PriceConfirmDialog info={flow.pending} … />`，不是条件渲染）。
   * 把 hook 放在早退之后，`info` 由空变有那一刻 hook 数量会变 ——
   * React 直接报 "Rendered more hooks than during the previous render"。
   */
  const [draft, setDraft] = useState<PromptDraft | null>(null)
  if (!info) return null
  // 逐张提示词。归一成数组，下面的 JSX 里少一层判空（`noUncheckedIndexedAccess` 没开，
  // 但 `info.shotPrompts?.length` 在三元里反复写会让每一支都得再断言一次）。
  const shotPrompts = info.shotPrompts ?? []
  const shotRefine = info.shotRefine

  /*
   * 草稿的取值判据 = **「这份草稿是不是当前这一份的」**，而不是用 `useEffect` 清空。
   *
   * 为什么不能用 effect：调用方「点生成 → 再看看 → 改参数 → 再点生成」时，
   * 弹窗组件**从来没有卸载过**（它一直挂着，只是 `info` 换了一份）。
   * 上一次编辑留在 state 里的话，用户看到的是**上一版的词**，
   * 而提交发出去的是 `args` 里按新参数重算的那一份 ——
   * 「预览的」与「实发的」不是同一份，这正是花钱按钮前面最不该破的那一条。
   *
   * 拿 `info` 的对象身份当下标：每次 `flow.request(...)` 都传一个新对象进来，
   * 「同一份 `info`」= 同一次提交确认。用 effect 清空会晚一帧，
   * 而那一帧里显示的恰好是上一版的词。
   */
  const shotTexts = draft?.src === info ? draft.shots : shotPrompts.map((s) => s.prompt)
  const baseText = draft?.src === info ? draft.base : info.prompt
  const writeShots = (next: string[]) =>
    setDraft({ src: info, shots: next, base: baseText })
  const writeBase = (next: string) => setDraft({ src: info, shots: shotTexts, base: next })

  /** 改过没有 —— 判据是**与模板原文逐字比**，不是与上一次草稿比 */
  const changedShots =
    shotPrompts.length > 0 && shotTexts.some((t, i) => t !== shotPrompts[i].prompt)
  const changedBase = shotPrompts.length === 0 && baseText !== info.prompt

  /**
   * 点「确认生成」。
   *
   * **只交用户真改过的那一支**：`shots` 与 `base` 互斥（哪一支在展示，
   * 用户改的就是哪一支），没改就给 `undefined`（见 `PromptEdits` 的说明）。
   */
  const submit = () =>
    shotPrompts.length > 0
      ? onConfirm(changedShots ? { shots: shotTexts } : undefined)
      : onConfirm(changedBase ? { base: baseText } : undefined)
  const rows: [string, ReactNode][] = [
    ['功能', info.title],
    info.platform ? ['投放平台', info.platform] : null,
    ['模型', modelLabel(info.model)],
    // 批量时这里必须写出「几个产品」，只报总张数的话用户没法核对
    // 「我是不是少导了一行」—— 而少一行就是少一笔钱。
    [
      '输出数量',
      info.batch
        ? `${info.batch.rows.length} 个产品 · 共 ${info.outputs} 张`
        : `${info.outputs} 个`,
    ],
    info.aspectRatio ? ['画面比例', info.aspectRatio] : null,
    info.resolution ? ['分辨率', info.resolution] : null,
    ['参考图', info.refImages ? `${info.refImages} 张` : '无'],
  ].filter(Boolean) as [string, ReactNode][]

  return (
    <Modal open onClose={onCancel} title="确认本次消耗" icon="coin" width={520}
      footer={
        <>
          <Button variant="outline" onClick={onCancel}>
            再看看
          </Button>
          <Button variant="primary" icon="check" onClick={submit}>
            确认生成
          </Button>
        </>
      }
    >
      <div className="stack">
        <table className="table">
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k}>
                <td style={{ width: 92, color: 'hsl(var(--muted-foreground))', fontSize: 'var(--text-xs)' }}>{k}</td>
                <td style={{ fontSize: 'var(--text-sm)' }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="price-row">
          <span className="muted text-xs">{info.basis ?? '按量计价'}</span>
          <span className={`price-row__value${info.free ? ' price-row__value--free' : ''}`}>
            {info.free ? '免费' : `¥${info.price.toFixed(2)}`}
          </span>
        </div>
        {/*
          提示词的**唯一展示处**。
          全站 7 个图片页 + 3 个视频/文案页曾在页面上各挂一份「提示词模板」卡，
          与这里的 <details> 是同一个东西的两份实现 —— 页面被撑长，还要改两处。
          现在统一收在这里：页面上不再出现提示词，需要看/复制就在**提交前这一步**看。
          复制按钮也放在这一层，删掉页面卡片不会丢复制能力。

          2026-10-07 又收进来一份：主图 / 详情模块那两档的**逐张提示词**
          （`shotPrompts`）。它们原先挂在「本批出图结构」卡头的一个弹层里，
          按用户口径「提示词隐藏也可以的」删掉了页面入口 —— 但它不能跟着被删：
          不接过来的话，这里展示的就只剩顶层那份**兜底通用词**，
          而实际发出去的是逐张 N 份，等于预览与实发不是同一份。
        */}
        {info.batch ? (
          <details>
            <summary className="text-xs muted" style={{ cursor: 'pointer' }}>
              查看这 {info.batch.rows.length} 行的完整提示词
            </summary>
            {/*
              批量行**刻意不做可编辑**：这一行是「该行全部张拼起来的词」（带分节），
              改一处**没法回填成 N 张** —— 硬拆是在猜分节符，答错一次就是
              把第 2 张的词塞给第 3 张，而两边都不报错。
              要逐张改，用单产品模式提交（那一支是逐张可编辑的）。
            */}
            <div style={{ marginTop: 8 }}>
              {/*
                逐行一层 <details>。为什么不直接平铺全部 pre：20 行 × 每行 1000+ 字
                会把弹窗撑成几屏，用户反而找不到「第几行不对」。
                行标题用产品名 —— 与对话框上方「N 个产品」对得上。
              */}
              <div className="stack" style={{ gap: 6 }}>
                {info.batch.rows.map((r, i) => (
                  <details key={`${r.label}-${i}`} className="batchrow__wrap">
                    <summary className="text-xs" style={{ cursor: 'pointer' }}>
                      <span className="muted">{i + 1}.</span> {r.label}
                      <span className="muted"> · {r.outputs} 张</span>
                    </summary>
                    <div style={{ marginTop: 6 }}>
                      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 6 }}>
                        <CopyButton text={r.prompt} label="复制这一行" />
                      </div>
                      <pre
                        className="text-xs"
                        style={{
                          whiteSpace: 'pre-wrap',
                          background: 'hsl(var(--muted))',
                          padding: 12,
                          borderRadius: 'var(--radius-sm)',
                          maxHeight: 220,
                          overflow: 'auto',
                          margin: 0,
                          fontFamily: 'var(--font-body)',
                        }}
                      >
                        {r.prompt}
                      </pre>
                    </div>
                  </details>
                ))}
              </div>
              <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
                <CopyButton
                  text={info.batch.rows
                    .map((r, i) => `【${i + 1}. ${r.label}】\n${r.prompt}`)
                    .join('\n\n' + '─'.repeat(48) + '\n\n')}
                  label="复制全部"
                />
              </div>
            </div>
          </details>
        ) : (
          <details>
            <summary className="text-xs muted" style={{ cursor: 'pointer' }}>
              {shotPrompts.length
                ? `查看 / 编辑这 ${shotPrompts.length} 张的逐张提示词`
                : `查看 / 编辑${info.promptLabel ?? '将发送给模型的完整提示词'}`}
            </summary>
            <div style={{ marginTop: 8 }}>
              {shotPrompts.length ? (
                /*
                 * 逐张那一支。**不做成 N 层 `<details>`**（批量那里是那么做的）：
                 * 批量的行数是「导了几行表格」、可能几十行，逐张一般 4–8 张，
                 * 一层列表直接铺完比再点一次更快看到全貌。
                 *
                 * 2026-10-08 起这里是**可编辑**的（用户口径「可以修改提示词」），
                 * 每张旁边还有一颗「AI 重写」。两点连带：
                 *   · 复制走的是 `shotTexts`（草稿）而不是 `s.prompt`（模板原文）——
                 *     复制按钮复制的必须是**将要发出去的那一份**；
                 *   · 「恢复原文」只在真改过之后才出现 —— 没改时它是个空操作，
                 *     摆在那儿只会让人怀疑「我什么时候改过？」。
                 */
                <>
                  <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 6 }}>
                    <CopyButton
                      text={shotTexts
                        .map((t, i) => `【${i + 1}. ${shotPrompts[i].label}】\n${t}`)
                        .join('\n\n' + '─'.repeat(48) + '\n\n')}
                      label="复制全部"
                    />
                  </div>
                  <ol className="batch__prompts-list">
                    {shotPrompts.map((s, i) => (
                      <li key={`${i}-${s.label}`} className="prompt-preview__item">
                        <div className="batch__prompts-idx">
                          第 {i + 1} / {shotPrompts.length} 张 · {s.label}
                          {shotTexts[i] !== s.prompt ? (
                            <span className="muted"> · 已改</span>
                          ) : null}
                        </div>
                        <div
                          className="row"
                          style={{ justifyContent: 'flex-end', gap: 6, marginBottom: 6 }}
                        >
                          {shotTexts[i] !== s.prompt ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              icon="refresh"
                              onClick={() =>
                                writeShots(shotTexts.map((v, k) => (k === i ? s.prompt : v)))
                              }
                            >
                              恢复原文
                            </Button>
                          ) : null}
                          {shotRefine ? (
                            <AiPromptButton
                              kind="image-shot"
                              brief={shotRefine.briefs[i] ?? ''}
                              prompt={shotTexts[i]}
                              sourcePage={shotRefine.sourcePage}
                              blocked={shotRefine.blocked}
                              onRefined={(text) =>
                                writeShots(shotTexts.map((v, k) => (k === i ? text : v)))
                              }
                            />
                          ) : null}
                          <CopyButton text={shotTexts[i]} label="复制这一张" />
                        </div>
                        {/*
                          提示词**直接可编辑**（用户口径「可以修改提示词」），
                          而不是给个「编辑」开关再切一次形态：这一块本来就是
                          「掏钱前最后核对一遍」，多一次点击只是多一处能忘掉的状态。
                          `data-testid` 给探针用 —— 探针要能**真的敲进一个字**
                          再点确认，才能证「改的那一份确实发出去了」。
                        */}
                        <Textarea
                          value={shotTexts[i]}
                          rows={8}
                          data-testid={`shot-prompt-${i}`}
                          onChange={(e) =>
                            writeShots(shotTexts.map((v, k) => (k === i ? e.target.value : v)))
                          }
                        />
                      </li>
                    ))}
                  </ol>
                </>
              ) : (
                <>
                  <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 6 }}>
                    {baseText !== info.prompt ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon="refresh"
                        onClick={() => writeBase(info.prompt)}
                      >
                        恢复原文
                      </Button>
                    ) : null}
                    <CopyButton text={baseText} label="复制提示词" />
                  </div>
                  {/* 与逐张那一支同一口径：可编辑，复制的与发出去的是同一份 */}
                  <Textarea
                    value={baseText}
                    rows={10}
                    data-testid="base-prompt"
                    onChange={(e) => writeBase(e.target.value)}
                  />
                </>
              )}
            </div>
          </details>
        )}
        <div className="text-xs muted">
          提交后可在右上角「任务中心」查看实时进度；失败不扣费，可直接重试。
        </div>
      </div>
    </Modal>
  )
}

/* ------------------------------ 空态占位 ------------------------------ */

export function PanelEmpty({ icon = 'sparkles', title, text }: { icon?: string; title: string; text?: string }) {
  return (
    <div className="card">
      <Empty icon={icon} title={title} text={text} />
    </div>
  )
}

/* ------------------------- 工作台右栏：结果与历史 ------------------------- */

/**
 * 生成页右栏的统一骨架。
 *
 * 10 个生成页的右栏本来是同一段 JSX 抄了 10 遍，差别只有三处：
 *   · 页签键（决定取哪一页的 jobs / outputs）
 *   · 产出形态（图片 / 视频走网格，文案走文本卡）
 *   · 中间那几张**页面专属解释卡**（分镜脚本 / 平台写作要点 / 尺寸参考 / 全部工作流）
 * 所以只有这三处留参数，其余全部内聚到这里。
 *
 * ⚠️ 两条必须守住的点：
 * 1. `jobs` 必须是**本页**的（调用方传 `jobsFor(page)`）。标题写着「本页历史」
 *    却混进别的页面的任务，比没有这块更糟。
 * 2. 「本页历史」原来只有 4 个页面有、6 个页面没有 —— 那是**手抄漏了**，不是有意设计。
 *    这里默认对所有页面开启。
 * 3. 「本页历史」必须走 `PagedList`，**不许在调用方 `slice()`**。
 *    原来这里是 `finished.slice(0, 4)` 硬截断 —— 数据还在，用户看不见，
 *    而且和「任务中心」的全量铺开是两种口径。分页只在这一处实现。
 * 4. **「本页历史」这张卡必须常驻** —— 不许再写 `finished.length ? <卡> : null`
 *    （2026-09-29 用户报障：「结果下面应该要有历史记录才对，这些页面都是」）。
 *
 *    原来的条件渲染看着「干净」：没记录就别占地方。实际是**用户分不清
 *    「这一页没有历史」和「这一页根本没有历史这个功能」** —— 三个页面
 *    （社媒图 / 图片处理 / 视频复刻）因为没有对得上的记录，整卡不渲染，
 *    功能明明做完了却像是没做。而同一个组件在别的页面又看得见，
 *    于是「有些页面有、有些没有」这个印象就被坐实了。
 *
 *    判据：**空态是一句话，缺功能是一个洞。** 前者用户读得懂，后者只能靠猜。
 *    ⚠️ 反向断言在 `scripts/smoke-job-history.ts`：这张卡不许再被条件包起来。
 *
 * 版面顺序（2026-10-08 起）：**合并卡（本轮产出 + 本页记录）在最上，页面专属解释卡垫底**。
 * 改前是「结果卡 → 专属卡 → 历史卡」三层，专属卡夹在中间；结果与历史并成一栏之后
 * 那个夹层不存在了，解释卡顺势退到最后 —— 它本来就是这一页最次要的信息。
 */
export function StudioPanel({
  jobs,
  outputs,
  onCancel,
  onClear,
  activeHint,
  kind = 'media',
  resultTitle,
  resultEmpty,
  aspectRatio,
  history = true,
  historyTitle = '本页历史',
  historyPageSize = 5,
  onPlayAudio,
  historyTabs,
  /**
   * 右栏**顶部**的自定义卡片（渲染在「进行中任务」条之后、历史卡之前）。
   *
   * 与下面的 `children` 分开是**位置**的需要：`children` 挂在整栏最底部，
   * 而视频页那张「成片提示词」卡必须一进页面就看得见 —— 它以前挂在 `children`，
   * 实测在 1440×900 下落在 y=984（首屏 1000px 装不下），用户口径
   * 「分镜脚本显示有点猥琐」。
   *
   * 为什么做成插槽而不是把那张卡搬进 StudioPanel：它读的是页面自己的
   * `prompt` / `form` / AI 状态，StudioPanel 是 9 个页面共用的组件，
   * 让它认识「提示词」等于给这个通用组件加一份只有一页用得上的领域知识。
   */
  head,
  children,
  extraResults,
}: {
  /** 本页任务，调用方用 `jobsFor(page)` 取 —— 传全局 jobs 会让「本页」变成谎话 */
  jobs: Job[]
  /** 本页本轮产出，调用方用 `outputsFor(page)` 取 */
  outputs: JobOutput[]
  onCancel: (id: string) => void
  /**
   * 收起「结果」栏的一批产出（结果卡上那颗「清空」）。**收的是 id 列表，不是页面键** ——
   * 由这里用**结果栏正在显示的那批** job（判据 `isResultJob`，与 `outputsFor` 共用）
   * 推导，调用方只传 `clearResults` 本身。
   * 若收页面键，调用方就得把 `jobsFor(X)` 的 X 再写一遍，那正是
   * 「读键写键不同源」事故的翻版（见 MEMORY 铁律）。
   *
   * ⚠️ **它不清理历史记录**（那走任务卡上的「移除记录」）。以前两者是同一个动作，
   * 于是用户在这张卡上点「清空」，右栏「本页历史」里的记录也跟着消失 ——
   * 按钮长在结果卡上、数字还是产出张数，读起来就是「清掉这一栏」
   * （2026-09-30 报障原话：「为什么点击清空了就没了，我明明清空是结果」）。
   */
  onClear?: (ids: string[]) => void
  /** 进行中任务卡的副标题；各页面耗时预期不同（90 秒 / 3-5 分钟 / 几十秒），故开放 */
  activeHint?: string
  /** 产出形态：媒体走可放大、可下载的网格；音频走播放器卡；文本走可直接编辑复制的卡片 */
  kind?: 'media' | 'audio' | 'text'
  resultTitle?: string
  resultEmpty?: string
  aspectRatio?: string
  /** 是否显示「本页历史」，默认显示 */
  history?: boolean
  /**
   * 历史卡的标题，默认「本页历史」。
   *
   * 页面里有多个工作流档位时，调用方传的是**收窄到当前档**的 `jobs`
   * （见 `JobCenterContext.jobsFor` 的第二参数），这时标题必须跟着变 ——
   * 卡片里只有当前档的记录，标题还写「本页」就是又一处名不副实。
   * 传 `${feature.name} 历史` 这类即可。
   */
  historyTitle?: string
  /** 历史列表每页条数；列表长度在页内可翻，不再硬截断 */
  historyPageSize?: number
  /**
   * 「这条历史里有一段能播的音频」时的回调 —— 传了才在任务卡头部出现那颗播放键。
   *
   * 判据在组件内部（`job.outputs` 里真有 `type === 'audio'` 的产出），
   * 调用方不需要自己筛一遍：调用方筛就得把「什么算能播」再写一份，
   * 而两份判据迟早会对不上（本项目最难查的那类）。
   */
  onPlayAudio?: (o: JobOutput) => void
  /**
   * 「本页历史」按**档位**分开看（2026-10-09）。
   *
   * 起因：用户在 `/digital-human` 里做了分镜图与配音，右栏一条都看不见
   * （用户口径「历史记录那边没出现怎么搞，我知道是不同类型」）。根因是
   * **一个页面里的三个档把记录写在了两种页面键下**（成片写 `digital-${mode}`、
   * 分镜图/配音写 `digital-human`），而右栏只读第一个键 ⇒ 后两档永远进不来。
   * `/video-studio` 同一件事：分镜参考图（`storyboard`）刻意不进右栏取数，
   * 因为它是视频任务的**输入**（混进「本轮结果」会让「生成了几支片子」变成假数字）。
   *
   * ⚠️ 所以**不是**「把后两档并进 `jobs`」：那会连结果栏的判据一起改掉
   * （`isResultJob` / 清空范围都从 `jobs` 推导），分镜图会混进「本轮产出」。
   * 这里只多给一条**分档的取数**，`jobs` 仍是「结果栏这一栏的任务」，一条没动。
   *
   * 每项：`id`（稳定的档位标识，**不是页面键** —— 页面切模式时它不许变，
   * 否则用户选的档会跟着跳）、`label`（档名，页面给）、`jobs`（这一档的任务全量，
   * 含在跑的；本组件负责滤掉在跑的、只把已完成的进列表）。
   *
   * 不传 = 现在的行为（一档一列表）。传了但只有一档 = 不渲染那排 chip
   * （一个选项的 chip-row 只是白占一行）。
   */
  historyTabs?: { id: string; label: string; jobs: Job[] }[]
  /**
   * 页面专属解释卡，排在**合并卡之后**（2026-10-08 起：结果与历史已并成一栏，
   * 原来是「结果之后、历史之前」，那个夹层已经不存在了）。
   *
   * ⚠️ 挂在整栏**最底部**：实测（1440×900）首屏只到 1000px，而合并卡自己就有
   * 583px 高 —— 挂在这里的卡要滚动才看得见。**「一进页面就该看到」的卡走上面
   * 的 `head`，不要挂这里**（视频页那张「成片提示词」就是这么搬上去的）。
   */
  children?: ReactNode
  /**
   * 右栏**顶部**的自定义卡片（渲染在「进行中任务」条之后、合并卡之前）。
   *
   * 与 `children` 分开只为一件事：**位置**。它比 `children` 早一屏出现，
   * 所以适合放「当前这套参数会怎么样」的实时读数（改左栏任何一页都跟着变）；
   * `children` 留给「解释性、可以往下翻」的内容。
   *
   * 为什么做成插槽而不是把那张卡搬进本组件：它读的是页面自己的 `form` / `prompt` /
   * AI 状态，而本组件是 9 个页面共用的 —— 让它认识「提示词」等于给通用组件
   * 塞一份只有一页用得上的领域知识。
   */
  head?: ReactNode
  /**
   * **附带产出段** —— 同一页里**另一个工作流**的产出（2026-10-09 加，为
   * `/social-image` 的「图文同出」：一次提交既出图又出配套文案）。
   *
   * 为什么不让调用方自己渲染一段塞进来：右栏是**一份实现**
   * （`PANEL_INTERNALS` 那条断言正是为此立的）—— 页面直接写 `TextResult`
   * 就成了第 11 份复制，而且「清空」的范围会漏掉这一段：`clearResults` 收的
   * id 由本组件**从正在显示的那批任务推导**，调用方自己拼 id 必然两处不一致。
   *
   * 每项三样，都是调用方知道的、本组件猜不出来的：
   *   · `label` —— 段标题（页面给，本组件不猜档名）；
   *   · `jobs` —— 这一档的任务，**只用于清空推导**（判据与主产出段同一个
   *     `isResultJob`：在跑的、没产出的都不该被收）；
   *   · `outputs` —— 要渲染的产出。
   *
   * 🔴 渲染按**每个 output 自己的 `type`** 分发，不复用 `kind`：`kind` 是
   * 一整栏一个值，而这一段里可能混着文本与图。`ResultGrid` 只认 image|video，
   * 混进去的文本会被**静默丢掉**（任务 succeeded、钱扣了、界面上什么都没有）——
   * 就是 `resultBody` 上面那段注释警告过的坑，在附带段这里一样不许踩。
   */
  extraResults?: { label: string; jobs: Job[]; outputs: JobOutput[] }[]
}) {
  /**
   * 「本页历史」的档位切换（2026-10-09）。起因见 `historyTabs` 参数上的注释。
   *
   * ⚠️ 选中值**只存内存**（`useState`），刻意不写 localStorage：写盘就要一个新 key，
   * 而「key 必须跟着档位清单一起升版」在本项目是有前科的（`step_<page>_vN` 那条：
   * 老值指向另一个档，页面照常渲染、截图上完全看不出来）。不存盘的最坏后果
   * 只是「换页回来回到第一档」，而第一档恰恰就是改前的行为。
   */
  const [histTab, setHistTab] = useState('')
  /** 当前显示的档 —— 传入的 id 找不着时回落到第一档（档位清单变了也不会得到空列表） */
  const histActive = historyTabs?.find((t) => t.id === histTab) ?? historyTabs?.[0] ?? null
  /**
   * 历史列表里显示的那批 —— 选中档的**已完成**任务；没传 `historyTabs` 时就是本页全量。
   *
   * ⚠️ 变量名必须仍是 `finished`（不要改成 `tabFinished` 之类）：
   * `scripts/smoke-job-history.ts` 靠 `items={finished}` 定位「历史列表那一段」，
   * 再检查里面的 `lean` / `drawerOnClick`。改名会让那两条断言变成
   * 「studio.tsx 里找得到历史列表」失败 —— 而它们守的是**每条记录都走紧凑行**
   * 这件真事，改个名字等于把这道网撤掉（本项目「改个名字把断言改瞎」的先例）。
   */
  const finished = (histActive ? histActive.jobs : jobs).filter((j) => !isActive(j.state))
  /**
   * 全部档的已完成记录条数 —— **卡头那行 hint 用它**（折叠态同源）。
   *
   * 为什么不用 `finished.length`：卡头说的是「这一栏有多少条记录」，而这一栏装的是
   * 全部档。写选中档的数字，切到「配音」时会看到「6 条记录」而分镜图那 4 条像是不存在 ——
   * 正是用户这次报的那个观感。分档明细由那排 chip 各自带在标签里，两边不重复表达同一件事。
   */
  const allFinished = historyTabs
    ? historyTabs.reduce((n, t) => n + t.jobs.filter((j) => !isActive(j.state)).length, 0)
    : finished.length

  /**
   * 「进行中任务」条吃**这一页全部档**在跑的任务。
   *
   * 传了 `historyTabs` 的页面，辅助档（分镜图 / 配音）在跑时只出现在页内某个格子的小字里，
   * 右栏这条唯一的「有事在跑」的提示看不见它们 —— 而它们照样占着上游、照样计费
   * （配音几秒、分镜图 40 秒）。这里按 **id 去重**后并起来：第一档就是 `jobs` 本身，
   * 不去重会渲染两张一模一样的卡。
   */
  const liveJobs = (() => {
    if (!historyTabs || historyTabs.length < 2) return jobs
    const seen = new Set(jobs.map((j) => j.id))
    const extra: Job[] = []
    for (const t of historyTabs) {
      for (const j of t.jobs) {
        if (seen.has(j.id)) continue
        seen.add(j.id)
        extra.push(j)
      }
    }
    return extra.length ? [...jobs, ...extra] : jobs
  })()

  /**
   * 历史卡的读取状态。
   *
   * 这四个值**直接从 Context 取，不走 props**：StudioPanel 的调用方有 9 个页面，
   * 每加一个 prop 就要 9 处各抄一遍 —— 而「哪些页面抄漏了」在本项目是有前科的
   * （历史块第一版就是 10 个页面里只有 4 个手抄了）。这里取的是**全局唯一**的
   * 历史读取状态，本来就不随页面而变，没有理由让调用方转述。
   *
   * 它解决的是一个**会说谎的空态**：
   *   · 还没读到 → 别说「没有任务」，说「正在读取」
   *   · 读失败   → 说清楚失败了 + 记录没丢 + 就地重试（`刷新`）
   *   · 真为空   → 才轮到那句「这里还没有跑过任务」
   * 用户 2026-09-29 报障「是不是只存在前端、清空就没了」，起点就是这里：
   * 后端没响应时界面照样笃定地说「你没跑过任务」。
   */
  const { historyLoaded, historyError, refreshHistory, hiddenCount, clearedResultIds } = useJobCenter()
  const [refreshing, setRefreshing] = useState(false)
  const historyState = !historyLoaded
    ? '正在从后端任务库读取记录…'
    : historyError
      ? `后端任务库读取失败：${historyError} —— 记录没有丢，点右上角「刷新」再试。`
      : null

  /**
   * 「本页历史」**默认展开**，点标题可收起（用户 2026-09-29 第二次发话）。
   *
   * 第一版是默认收起，理由是「右栏最次要的信息，展开能占掉半屏」。
   * 用户随即报障：「结果下面应该要有历史记录才对」—— 收起态只剩一行
   * 「共 N 条 · 点击展开」，读起来**像没有这块**。历史是复用上次参数重跑、
   * 回看失败原因、找回三天前那批图的唯一入口，不是可选的装饰。
   * 「右上角任务中心」能看全局队列，但那是**跨页面的总账**，
   * 找「这一页刚跑的那批」永远要回来这里 —— 所以它得先看得见。
   *
   * 折叠能力**保留**（用户当时原话是「点击后可以展开」，可折叠这件事没被撤），
   * 只是默认值反过来。
   *
   * ⚠️ key 必须升 `_v2`：默认值改了，但老用户浏览器里存着 `false`，
   * 不改 key 的话**报障的这个页面恰恰永远不会变**（其他页面倒是会变，
   * 因为那个键是全局单键）—— 这类「升级对老数据不生效」的坑本项目踩过，
   * 冒烟断言会检查这个 key 名。
   *
   * 为什么仍是**全局单键**而不是按页存：按页存就要调用方再传一个 pageKey，
   * 又是一个「必须和 jobsFor 保持同步」的键（本项目的头号 bug 类型）。
   * 折叠偏好本来也是人的习惯，不是页面的属性 —— 在一页展开过，别的页也该展开。
   */
  const [histOpen, setHistOpen] = useLocalStorage('history_open_v2', true)

  /*
   * 结果卡上那颗「清空」收的是**结果栏这一栏**（`isResultJob` = 本机、有产出的那几条），
   * 不是「本页已完成记录」—— 历史记录一条都不动。
   *
   * 这里刻意不拿 `finished`（会把没有产出的失败记录也算进来）：那样点一次「清空结果」，
   * 顺手把几条**没显示在结果栏**的失败记录也标成「已收起」，用户下次找它们时会以为丢了。
   * 清空了什么的判据，必须和结果栏显示了什么**是同一个**。
   */
  /**
   * 附带产出段的产出总数（`extraResults`）。
   *
   * 它出现在三个判据里：产出段要不要撑起来（主产出为 0 时）、
   * 「刚被收起」那句提示要不要说、以及**清空**的范围。现算一次，三处共用。
   */
  const extraTotal = (extraResults ?? []).reduce((n, er) => n + er.outputs.length, 0)
  /**
   * 「清空」收的那批 = **主产出段 + 附带产出段**。
   *
   * 附带段（`extraResults`）是同一页里另一个工作流的产出，它在这一栏里**看得见**
   * ——点清空时漏下它，「图没了、文案还在」会被读成没清干净。
   * 判据仍是 `isResultJob`（本机 + 有产出），与主段**同一个**，不另立标准。
   *
   * ⚠️ 卡头那颗按钮上的数字仍只报主产出（`outputs.length`）：那是这一栏的「主体产出数」，
   * 而附带段有自己的段标题。数字与清空范围在这件事上刻意不强行对齐 ——
   * 对齐就得让本组件去猜附带段是什么，那是页面的知识。
   *
   * ⚠️ 主段这一行**必须仍写成 `jobs.filter(isResultJob)`**：`smoke-job-history.ts`
   * 靠这个字面钉住「结果栏显示哪些」与「清哪些」是**同一个判据**。合成一句
   * `[...jobs, ...extra].filter(...)` 会让那条断言找不到锚点（而它守的是真事）。
   */
  const shownResults = jobs.filter(isResultJob)
  const extraResultJobs = (extraResults ?? []).flatMap((er) => er.jobs).filter(isResultJob)
  /** 清空的实际范围：主段 + 附带段，判据同一个 `isResultJob` */
  const clearable = [...shownResults, ...extraResultJobs]
  const clear = onClear && clearable.length ? () => onClear(clearable.map((j) => j.id)) : undefined

  /**
   * 结果栏空着，但**空的原因是「刚被收起」** —— 那就不能说「还没有结果」。
   *
   * 判据取 `clearedResultIds` 而不是「`jobs` 里有产出但 `outputs` 是空的」：
   * 后者依赖「调用方给 `jobs` / `outputs` 传的是同一档」这个前提（页面合并过五次，
   * 这个前提不是白给的）。直接问「这批任务里有谁是刚被收起的」，
   * 与结果栏为什么会空**同源**，不会误报。
   *
   * 不说这句话的代价很具体：用户刚点完「清空」，结果栏回到「还没有结果 + 去上传商品图」，
   * 读起来还是「清了就没了」—— 2026-09-30 那轮报障就是这个观感。
   *
   * ⚠️ 附带产出还有东西时不走这一支（那时结果栏并不空，见 `resultSection`）。
   */
  const resultsCleared =
    !outputs.length && !extraTotal && clearable.some((j) => clearedResultIds.has(j.id))
  const clearedHint =
    '本轮结果已收起 —— 产出没有被删：历史里点开任意一条仍能看到，想放回来点提示里的「撤销」。'

  /**
   * 产出一段 —— 右栏**合并卡里的第一段**（2026-10-08 起，结果与历史并成一栏）。
   *
   * 一律 `bare`：不带走自己的卡片外壳与标题头。一张卡里再套一张同款卡，
   * 标题就会写两遍、边框套两层 —— 那正是这次要拆掉的东西。
   * 「共 N 个」与「清空」现在由**外面那张卡的头**承担，这里只负责把产出摆出来。
   *
   * ⚠️ 底下这三个分支**不许合并成一个**：`ResultGrid` 的过滤条件是
   * `image || video`，音频塞进去会被**静默丢掉**（任务 succeeded、钱扣了、
   * 右栏写着「共 0 个」）。新增产出类型时这里必须同步加分支。
   */
  const resultBody =
    kind === 'text' ? (
      <TextResult outputs={outputs} bare onClear={clear} />
    ) : kind === 'audio' ? (
      <AudioResult outputs={outputs} bare onClear={clear} onPlay={onPlayAudio} />
    ) : (
      <ResultGrid outputs={outputs} bare aspectRatio={aspectRatio} onClear={clear} />
    )

  /**
   * 附带产出段的渲染 —— 按**每个 output 自己的 `type`** 分发。
   *
   * 与 `resultBody` 那三个分支**同源、判据不同**：`resultBody` 是按页面声明的
   * `kind` 去渲染一栏（一栏一种产出），这里对着事实说话（每个 output 自己是什么）。
   * 三种类型一个都不许漏：`ResultGrid` 只认 `image|video`、`AudioResult` 只认
   * `audio`、`TextResult` 只认 `text` —— 少写一支，那一类产出就是
   * 「任务 succeeded、钱扣了、界面上没有」。
   */
  const renderOutputs = (list: JobOutput[]) => {
    const media = list.filter((o) => o.type === 'image' || o.type === 'video')
    const audios = list.filter((o) => o.type === 'audio')
    const texts = list.filter((o) => o.type === 'text')
    return (
      <>
        {media.length ? <ResultGrid outputs={media} bare aspectRatio={aspectRatio} /> : null}
        {audios.length ? <AudioResult outputs={audios} bare /> : null}
        {texts.length ? <TextResult outputs={texts} bare /> : null}
      </>
    )
  }

  /** 附带产出段（每段自带小标题；空段整段不渲染） */
  const extraSegs = (extraResults ?? [])
    .filter((er) => er.outputs.length)
    .map((er) => (
      <div className="panel-flow__seg" key={er.label}>
        <div className="panel-flow__label">{er.label}</div>
        {renderOutputs(er.outputs)}
      </div>
    ))

  /**
   * 产出段**什么时候才出现**。
   *
   * 三种情况各有各的处理，关键是第三种：**没有产出时整段不渲染**。
   * 改前结果栏是一张独立的卡，空着也要占半屏（`outputsFor` 刻意只收本机本轮产出，
   * 所以进页面时「共 0 个」是常态），而它下面那张历史卡里明明躺着十几条 ——
   * 用户看到「上面一张空卡、下面一张有记录的卡」，自然会问这两块有什么区别。
   */
  const resultSection = outputs.length ? (
    <div className="panel-flow__seg">
      <div className="panel-flow__label">
        {resultTitle ?? '本轮产出'}
        <span className="muted"> · {outputs.length} 个</span>
      </div>
      {resultBody}
      {/* 附带产出段紧跟在主段之后（`/social-image` 的图与配套文案）——
          同一层壳里，与历史之间不出现第二张卡 */}
      {extraSegs}
    </div>
  ) : extraTotal ? (
    /*
     * 主产出还没回来、附带产出**先到了**：`/social-image` 的文案 12 秒就出，
     * 图要 60 秒。这一支不成立的话，用户等文案跑完却在右栏看不见它
     * （只有「进行中任务」那条先消失），只能去历史里翻。
     * 这里不另加外层标题：每段自己带「配套文案 · 小红书种草文案」。
     */
    <>{extraSegs}</>
  ) : resultsCleared ? (
    /* 刚点过「清空」：说清楚**只是收起了预览**，产出一条都没删 */
    <div className="panel-flow__seg text-xs muted" style={{ padding: '2px 2px 6px' }}>
      {clearedHint}
    </div>
  ) : null

  /**
   * 档位 chip-row —— 只在**真有两档以上**时渲染（一个选项的 chip-row 只是白占一行）。
   *
   * 条数写在标签里（`配音 · 6`）而不是只写档名：切到「配音」之后，另外两档各有几条
   * **仍然看得见** —— 这正是这次报障的起点（用户不知道那两档的记录其实在库里）。
   * 数字从传进来的 `jobs` **现算**，页面不许自己再数一遍；两处表达同一件事迟早对不上。
   *
   * 走全站唯一的 `Chips`，不自己拼 `<button className="chip">`：chip 的选中态、
   * hover、`data-active` 都只有那一份实现（本项目铁律 3）。
   */
  const historyTabBar =
    historyTabs && historyTabs.length > 1 ? (
      <Chips
        className="chips panel-history__tabs"
        value={histActive?.id ?? ''}
        onChange={setHistTab}
        options={historyTabs.map((t) => {
          const n = t.jobs.filter((j) => !isActive(j.state)).length
          return { value: t.id, label: n ? `${t.label} · ${n}` : t.label }
        })}
        titles={Object.fromEntries(
          historyTabs.map((t) => [
            t.id,
            `${t.label} · 共 ${t.jobs.filter((j) => !isActive(j.state)).length} 条记录`,
          ]),
        )}
      />
    ) : null

  /** 历史列表本体 —— 两种版面共用，只差外面那层壳（卡片 vs 页签正文）。 */
  const historyList = (
    <>
      {historyTabBar}
      {/* 读取状态先说清楚，再谈有没有记录 —— 见下方 empty 的注释 */}
      {historyState ? (
        <div className="text-xs muted" style={{ padding: '4px 2px' }}>
          {historyState}
        </div>
      ) : null}
      <PagedList
        items={finished}
        pageSize={historyPageSize}
        pageSizes={[5, 10, 20]}
        unit="条"
        /*
         * ⚠️ **只在「确实读到了、而且确实为空」时才说「这里还没有跑过任务」**。
         *
         * 这句话以前是无条件写的，于是后端没响应 / 还没拉回来时，界面照样
         * 断言「你没有跑过任务」—— 而库里可能躺着几十条。用户看到一页空白 +
         * 这句笃定的话，自然得出结论「数据只在前端，清了就没了」
         * （2026-09-29 报障原话）。
         *
         * 判据：**不确定的时候不许替用户下结论**。
         */
        empty={
          historyState ? null : finished.length === 0 && allFinished > 0 ? (
            /* 选中的这一档没有记录，但**这一页别的档有** —— 这时说「这里还没有跑过任务」
               是假话（用户很可能刚在别的档跑完切过来），而假话只有一种读法：记录丢了。
               一个字都不多讲，直接指到上面那排 chip 上。 */
            <div className="text-xs muted" style={{ padding: '4px 2px' }}>
              这一档还没有跑过任务 —— 上面切到别的档看看，那一档的记录还在。
            </div>
          ) : hiddenCount > 0 ? (
            /* ⚠️ 有已清理的记录时**不许**说「这里还没有跑过任务」—— 用户刚清过，
               那句话就是假的，读出来只有一种结论：记录被删了。
               （2026-09-30 那轮报障正是从这句笃定的话读出来的。）
               这里改成说三件事：只是被清理、库里没删、下面那行能把它们放回来。
               ⚠️「这里没有可见的记录」这句**是断言锚点**（`smoke-job-history.ts`
               逐字找它）—— 可以往后删字，但不许改这十一个字。
               2026-10-09「字别太多」：条数交给下面那行（那里本来就在报数字），
               这里只说「没删、点下面」—— 同一件事不在两处各说一遍。 */
            <div className="text-xs muted" style={{ padding: '4px 2px' }}>
              这里没有可见的记录 —— 清理过的<b>没有被删除</b>，下面点「恢复」就回来了。
            </div>
          ) : (
            <div className="text-xs muted" style={{ padding: '4px 2px' }}>
              {/* 「本页」改「这里」：历史被收窄到当前档的页面（商品图 / 详情页）
                  上，写「本页还没有跑过任务」是假话 —— 别的档跑过。 */}
              这里还没有跑过任务。
              {/* 页面传了 `resultEmpty`（「左侧填好参数后点生成」那类引导）就用它 ——
                  一栏合并之后，「没跑过任务」与「怎么开始」本来就是同一个空态。 */}
              {resultEmpty
                ? ` ${resultEmpty}`
                : ' 提交后记录会留在这里 —— 可以看失败原因、按原参数重试，点任意一条会在右侧展开产出。'}
            </div>
          )
        }
        /* `drawerOnClick`：整条点击 = 右侧抽屉（用户 2026-09-29 要求）。
           历史列表是**回看**场景，详见 components/TaskCard.tsx 文件头结论 3。

           `lean`：右栏这一列只有 300~380px，默认那版一条要铺三行小字 + 文字按钮，
           十条就是一屏半。改成紧凑行（两行 + 图标动作）—— 2026-10-08 用户口径
           「这种简约」「字体间距大力模仿」。**信息一个没少**：点整条进抽屉，
           模型名 / 来源页 / 任务号 / 提示词全在那一层。 */
        render={(j) => (
          <TaskCard
            key={j.id}
            job={j}
            onCancel={onCancel}
            drawerOnClick
            lean
            onPlayAudio={onPlayAudio}
          />
        )}
      />
      {hiddenCount > 0 ? (
        /* 「清理可逆」这件事得**点得到**，不能只是小字说明。
           用户 2026-09-30 就是在这行字前面站了一次 —— 看见了却没意识到
           那里可以恢复。所以这里给一颗真链接，直落任务中心的「已清理」。
           `hiddenCount` 是**全库**的口径（`hidden=only` 的总数），不是本页的，
           所以留着「库里」（别让人以为这个数字属于本页）。
           ⚠️ 2026-10-09 用户口径「字别太多」，砍成 14 字 —— 砍掉的是
           「共有…的记录」这种把数字裹起来的壳，**两个事实一个没丢**：
           「已清理 N 条」= 有多少条没显示，「不会被删除」= 钱和文件都还在
           （后者是 2026-09-30 那次报障换来的，不许当装饰删）。 */
        <div
          className="text-xs muted"
          style={{ padding: '6px 2px 2px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
        >
          <span>
            库里已清理 {hiddenCount} 条（<b>不会被删除</b>）
          </span>
          <Link className="btn btn--outline btn--sm" to="/jobs?filter=hidden">
            恢复
          </Link>
        </div>
      ) : null}
    </>
  )

  /* 「刷新」两种版面共用：堆叠版在卡头，页签版在页签栏右侧。
     读取失败时用户必须能就地重试，而不是刷新整页（刷新会丢掉左栏填到一半的表单）。
     ⚠️ 文案 2026-10-09 由「重新读取」砍成「刷新」（用户口径「字别太多」）：
     卡头一行里已经挤着标题 + hint + 清空 + 这颗按钮，4 个字在这条 300~380px 的
     栏里是实打实的宽度。**意思一点没少** —— 图标是 refresh、`title` 仍是
     「从后端任务库重新读取记录」（悬停就能读到全话），按钮本身只留动词。
     改这个字要连改三处断言：`smoke-job-history.ts`（源码 grep 这串字）、
     `verify-job-store.py`（按 `textContent` 找这颗按钮、真点它）。 */
  const refreshBtn = (
    <Button
      variant="ghost"
      size="sm"
      icon="refresh"
      loading={refreshing}
      title="从后端任务库重新读取记录"
      onClick={() => {
        setRefreshing(true)
        void refreshHistory().finally(() => setRefreshing(false))
      }}
    >
      刷新
    </Button>
  )

  /*
   * 「结果」与「历史」**合并成一栏**（2026-10-08 用户要求，照 minimax.cn 的右栏）。
   *
   * 改前是两张卡上下叠：「XXX 结果」（本轮产出网格）+「XXX 历史」（本页全部记录）。
   * 症结不是「重复」，而是**空的结果卡会单独占掉半屏**：`outputsFor` 刻意只收
   * 本机本轮的产出（从后端读回的远端记录不算），所以刚进页面时「共 0 个」是常态，
   * 而它下面那张历史卡里明明躺着十几条 —— 用户读到的就是「上面一张空卡、
   * 下面一张有记录的卡」，只剩一种结论：这两块到底什么关系。
   *
   * 现在只有**一张卡**：产出段（有就铺开、没有就整段消失）直接接着历史列表，
   * 中间没有第二道卡头与边框。
   *
   * ⚠️ **两个口径没有被合并，将来也不许合并**：
   *   · 产出段 = 本机本轮的实时产出（`outputsFor`，**排除远端**）；
   *   · 历史列表 = 本页全部记录（含从后端任务库读回的远端记录）。
   * 把它们并成一个「只显示最新一批」的列表，等于一进页面就铺几十张历史图 ——
   * 那正是 `outputsFor` 里特意写明不许发生的事。
   *
   * ⚠️ 卡头标题仍走 `historyTitle`（页面把 jobs 收窄到当前档时标题要跟着变），
   * 标题写着「历史」而这一栏里还装着本轮产出 —— 全靠卡头那行 hint 说清楚
   * 「N 个产出 · M 条记录」。别把 hint 去掉，那是这一栏唯一的目录。
   *
   * ⚠️ **产出为 0 时不许写「0 个产出」**（2026-10-09 用户口径「字别太多」）：
   * 那半句在说「这栏里没有产出」，而这一栏里没有的东西不必占字 —— 刚进页面时
   * `outputsFor` 恒为 0（见上），于是卡头**天天**挂着「0 个产出 · 23 条记录」，
   * 用户读到的就是「一行里有一半是废话」。产出真有时才两样都写。
   */
  return (
    <>
      <ActiveJobsStrip jobs={liveJobs} onCancel={onCancel} emptyHint={activeHint} />

      {/* 页面自己的一栏（视频页的「成片提示词」卡）。位置在历史之上：
          它是「当前这套参数会拍成什么」的实时读数，改任何一页都会跟着变 ——
          而历史是回看，第一眼要看的不是它。见 `head` 的说明。 */}
      {head}

      {/* 见文件头结论 4：常驻，不判空 */}
      {history ? (
        <Card className={histOpen ? '' : 'card--folded'}>
          <CardHead
            title={historyTitle}
            icon="clock"
            open={histOpen}
            onToggle={() => setHistOpen((v) => !v)}
            hint={
              histOpen
                ? outputs.length
                  ? `${outputs.length} 个产出 · ${allFinished} 条记录`
                  : `${allFinished} 条记录`
                : `${allFinished} 条 · 点击展开`
            }
            right={
              <>
                {/* 清空**只收产出预览**，历史记录一条都不动 —— 见 `onClear` 上的注释 */}
                {clear ? (
                  <ClearButton
                    count={outputs.length}
                    unit="个产出"
                    title="收起这一栏的产出预览（历史记录不动，点开任意一条仍能看到）"
                    onClear={clear}
                  />
                ) : null}
                {refreshBtn}
              </>
            }
          />
          {histOpen ? (
            <CardBody tight>
              {resultSection}
              {historyList}
            </CardBody>
          ) : null}
        </Card>
      ) : (
        /* `history={false}` 的页面（当前 10 个生成页都没有这么用）退回「只有产出」 */
        resultSection
      )}

      {children}
    </>
  )
}

export type { JobOutput }

/* ------------------------------------------------------------------------ */
/* 主图配比 · 每类各出几张（2026-10-07）                                      */
/* ------------------------------------------------------------------------ */

/**
 * 「白底 / 场景 / 卖点 / 细节」各出几张 —— 主图档**唯一的张数入口**。
 *
 * 用户口径（2026-10-07）：参考竞品那块「自定义配置」（白底图 1 / 场景图 3 /
 * 卖点图 3 / 其他 0，至少 7 张），选了「补上，可自由配比」。
 *
 * ⚠️ 它取代了张数输入框，而不是与它并存 —— 两个控件写同一个量迟早对不上：
 * 用户把「场景」加到 3，上面输入框还写着 4，提交的是 7 张还是 4 张就没人说得清。
 * 所以调用方在主图档**必须收起 `CountInput`**（`/detail-page` 与 `/batch` 都改过）。
 *
 * 每类用 `×N` 而不是「第 N 张」：这里回答的是「这一类要几张」，
 * 逐张展开的名字由 `mainShotsFor` 负责（同一类多张会带「第 N 张 · 换机位」）。
 *
 * 减到 0 张是合法的（这一类不出），但**总和至少 1 张** —— `bumpMix` 会拒绝
 * 把最后一张减没，否则整批变成 0 张、报价 0 元，而按钮照常可点。
 */
export function ShotMixPicker({
  mix,
  onChange,
}: {
  mix: MainMix
  onChange: (m: MainMix) => void
}) {
  const counts = normalizeMix(mix)
  const total = mixTotal(counts)
  return (
    <div className="shotmix">
      {MAIN_SHOTS.map((s, i) => (
        <div className="shotmix__row" key={s.name}>
          <div className="shotmix__info">
            <span className="shotmix__name">{s.name}</span>
            <span className="shotmix__desc">{s.task}</span>
          </div>
          <div className="shotmix__ctrl">
            {/*
              ⚠️ 减号的禁用条件有**两条**，少写一条就会出现「按钮亮着、点了一点反应没有」：
                · 本类已经是 0 张；
                · 整批只剩 1 张 —— `bumpMix` 会拒绝这次改动（0 张 = 报价 0 元、
                  按钮却还能点）。拒绝是对的，但**按钮必须同时变灰**，
                  否则用户只会以为控件坏了。
            */}
            <Button
              size="sm"
              variant="outline"
              disabled={counts[i] <= 0 || total <= 1}
              title={counts[i] <= 0 ? `${s.name} 已经是 0 张` : total <= 1 ? '整批至少保留 1 张' : undefined}
              aria-label={`${s.name} 减少一张`}
              data-testid={`mix-minus-${i}`}
              onClick={() => onChange(bumpMix(counts, i, -1))}
            >
              −
            </Button>
            <b className="shotmix__num" data-testid={`mix-count-${i}`}>
              {counts[i]}
            </b>
            <Button
              size="sm"
              variant="outline"
              disabled={total >= MAX_BATCH_COUNT}
              title={total >= MAX_BATCH_COUNT ? `整批最多 ${MAX_BATCH_COUNT} 张` : undefined}
              aria-label={`${s.name} 增加一张`}
              data-testid={`mix-plus-${i}`}
              onClick={() => onChange(bumpMix(counts, i, 1))}
            >
              +
            </Button>
          </div>
        </div>
      ))}
      <div className="shotmix__total" data-testid="mix-total">
        当前共 <b>{total}</b> 张
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/* 包含模块 · 逐模块勾选（2026-10-07）                                        */
/* ------------------------------------------------------------------------ */

/**
 * 「本次要哪几个官方模块」—— 平台详情模块档的勾选列表。
 *
 * 用户口径（2026-10-07）：参考竞品那块「包含模块 已选 0/16」，
 * 选了「加 16 个模块逐个勾选，替换掉现在的三档预设」。
 *
 * 🔴 **列表不是固定 16 项** —— 竞品那 16 条是它自己的固定清单，
 * 我们的模块**跟着平台规格走**：Amazon 基本档 5、淘宝 8、TikTok Shop 8…
 * 所以这里渲染的是 `spec.modules`，页面上不许出现第二份写死的模块名。
 *
 * 空数组 = 全选（见 `normalizeModules`）。取消到最后一个时会归一化回全选，
 * 因此**永远至少有一个模块被勾上** —— 空清单会让这一档一张图都不出，
 * 而界面上一切正常，属于静默的零产出。
 */
export function ModulePicker({
  spec,
  counts,
  withMobile,
  onChange,
}: {
  spec: DetailSpec
  /**
   * 模块名 → **份数**（完整表；0 = 这个模块不出）。
   *
   * ⚠️ 是「份数」不是「张数」。官方多图模块（A+ 三图 / 单图 + 侧栏）**一份顶
   * 3 张、2 张**，`withMobile` 打开后全宽类每份还要再加一张。换算走
   * `moduleShotCount`，界面显示的是张数、± 调的是份数，两个量不能混
   * （混了的表现是「勾一个模块只出 1 张」，不报错）。
   */
  counts: Record<string, number>
  /** 打开移动端资产后，全宽类模块每份多一张 —— 张数徽标要跟着变 */
  withMobile?: boolean
  onChange: (next: Record<string, number>) => void
}) {
  const on = spec.modules.filter((m) => (counts[m.name] ?? 0) > 0)
  const total = spec.modules.reduce(
    (n, m) => n + (counts[m.name] ?? 0) * moduleShotCount(m, withMobile),
    0,
  )
  return (
    <div className="modpick" data-testid="modpick">
      <div className="modpick__head">
        <span className="text-xs muted">
          已选 {on.length}/{spec.modules.length} 个模块 · 合计 {total} 张
        </span>
        <button
          type="button"
          className="linkbtn"
          /* 「全选」= 每个模块各 1 份（不是把份数拉满）—— 用户点它是想「都要」，
             不是想「每样都要 3 份」。份数拉满会让报价当场翻三倍。 */
          onClick={() => onChange(Object.fromEntries(spec.modules.map((m) => [m.name, 1])))}
        >
          全选
        </button>
      </div>
      {/*
        一行一条 —— 与同一屏的 `ShotMixPicker`（各类张数）**逐条对齐**：
        同样的内距、同样粗细的分隔线、同样的「左侧说明 + 右侧控制区」。
        两块是同一件事在两种档位下的面孔，长得不一样就像两个页面拼的。

        ⚠️ 顺序：勾选框从**行首**挪到**行尾的控制区**，2026-10-08 又从控制区
        整体换成 `[−] [N 张] [+]` —— 与 `ShotMixPicker` 的 `[−] [N] [+]` 同一个位置。
        控制件右对齐成一条竖线：左边读、右边操作。
      */}
      <div className="modpick__list">
        {spec.modules.map((m, i) => {
          const n = counts[m.name] ?? 0
          const per = moduleShotCount(m, withMobile)
          const off = n <= 0
          const full = n >= MODULE_MAX_TIMES
          return (
            <div className="modpick__item" key={m.name} data-active={!off} data-testid={`mod-row-${i}`}>
              <span className="modpick__text">
                <span className="modpick__name">{m.name}</span>
                <span className="modpick__task">{m.task}</span>
              </span>
              <span className="modpick__ctrl">
                {/*
                  ⚠️ 减号的禁用条件与 `ShotMixPicker` 同一条：**0 份时变灰**。
                  不减到 -1（`planDetailShots` 会把负数当 0，界面却显示 -1 张）。
                */}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={off}
                  title={off ? `${m.name} 已经是 0 份（这个模块不出）` : `少出一份「${m.name}」`}
                  aria-label={`${m.name} 减少一份`}
                  data-testid={`mod-minus-${i}`}
                  onClick={() => onChange({ ...counts, [m.name]: n - 1 })}
                >
                  −
                </Button>
                <b className="modpick__slots" data-testid={`mod-count-${i}`}>
                  {n * per} 张
                </b>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={full}
                  title={full ? `一个模块最多 ${MODULE_MAX_TIMES} 份` : `多出一份「${m.name}」`}
                  aria-label={`${m.name} 增加一份`}
                  data-testid={`mod-plus-${i}`}
                  onClick={() => onChange({ ...counts, [m.name]: n + 1 })}
                >
                  +
                </Button>
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

